<template>
    <div>
        <base-dialog :loading="loading" :value="dialog" :width="560" :title="$t('common.list_evaluations')" @input="onInput" @close="hide">
            <skeleton-loader v-if="loading" type="rows" />
            <template v-else>
                <div class="tpl-list">
                    <div v-for="evaluation in currentEvaluations" :key="evaluation.id" class="tpl-row">
                        <div class="tpl-info">
                            <span class="tpl-name">{{ evaluation.name }}</span>
                            <span class="tpl-count">
                                {{ evaluation.evaluations }} {{ $t('common.evaluations').toLowerCase() }}
                            </span>
                        </div>
                        <div class="tpl-actions">
                            <button type="button" class="icon-btn" @click="editTemplate(evaluation)">
                                <v-icon size="16">mdi-pencil-outline</v-icon>
                            </button>
                            <button type="button" class="icon-btn danger-btn" @click="deleteTemplate(evaluation)">
                                <v-icon size="16">$delete</v-icon>
                            </button>
                        </div>
                    </div>
                    <div v-if="currentEvaluations.length === 0" class="tpl-empty">
                        {{ $t('common.not_found') }}
                    </div>
                </div>
                <div class="tpl-note">
                    <v-icon size="14">mdi-information-outline</v-icon>
                    <span>{{ $t('common.max_evaluations_reminder') }}</span>
                </div>
            </template>
        </base-dialog>

        <create-evaluation-dialog :coachId="coachId" ref="evaluation" is-second-dialog />

        <kimsa-confirm ref="delete" :title="$t('common.delete_template')" is-second-dialog
            :content="$t('common.delete_template_content')" :persistent="false" :loading="loadingDelete"
            @confirm="deleteEvaluation" @cancel="evaluationId = null" :close-on-confirm="false" :alert-mode="true" />
    </div>
</template>

<script>
import CreateEvaluationDialog from "@/components/coach/CreateEvaluationDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import BaseDialog from '@/components/common/BaseDialog';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';

export default {
    components: { KimsaConfirm, CreateEvaluationDialog, BaseDialog, SkeletonLoader },
    name: "EvaluationsListDialog",
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            evaluationId: null,
            loadingDelete: false,
        }
    },
    computed: {
        ...mapState(['evaluations']),
        disabled(vm = this) {
            return !vm.selectedEvaluation
        },
        currentEvaluations(vm = this) {
            return vm.evaluations?.map(evaluation => ({ ...evaluation, id: evaluation.id, evaluations: evaluation.config.length })) || []
        }
    },
    async mounted() {
        const vm = this
        await vm.loadEvaluations()
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        show(currentId) {
            const vm = this
            vm.currentId = currentId
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
        },
        onInput(val) {
            if (!val) this.hide()
        },
        async loadEvaluations() {
            const vm = this;
            await vm.withLoading(async () => {
                if (vm._checkNeedReloadBind(vm.evaluations, vm.coachId)) await vm.bindEvaluations(vm.coachId)
            })
        },
        editTemplate(evaluation) {
            const vm = this
            vm.$refs.evaluation.loadEvaluation(evaluation)
        },
        deleteTemplate(evaluation) {
            const vm = this
            vm.evaluationId = evaluation.id
            vm.$refs.delete.show()
        },
        async deleteEvaluation() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('evaluations').doc(vm.evaluationId).delete()
                let assignedRef = await db.collection('assignedEvaluations')
                    .where('evaluationId', '==', vm.evaluationId).get()
                await Promise.all(assignedRef.docs.map(async assigned => {
                    const data = assigned.data()
                    if (data.studentId) await db.collection('users').doc(data.studentId).update({ assignedEvaluationId: null })
                    else if (data.teamId) await db.collection('teams').doc(data.teamId).update({ assignedEvaluationId: null })
                    else if (data.groupId) await db.collection('groups').doc(data.groupId).update({ assignedEvaluationId: null })
                    await db.collection('assignedEvaluations').doc(assigned.id).delete()
                }))
                vm.toast(vm.$t('common.template_deleted'))
                vm.evaluationId = null
                vm.$refs.delete?.hide()
            }, 'loadingDelete')
        },
    },
    watch: {
        coachId() {
            this.loadEvaluations()
        },
    }
}
</script>

<style scoped>
.tpl-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.tpl-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    background: var(--v-surface-base);
    transition: border-color 0.15s, background 0.15s;
}

.tpl-row:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface2-base);
}

.tpl-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.tpl-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tpl-count {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.tpl-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
}

.icon-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtMuted-base);
    transition: background 0.12s, color 0.12s;
}

.icon-btn:hover {
    background: var(--v-outline2-base);
    color: var(--v-txtPrimary-base);
}

.icon-btn.danger-btn:hover {
    background: rgba(232, 92, 92, 0.12);
    color: var(--v-error-base);
}

.tpl-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.tpl-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 16px;
    padding: 10px 12px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.tpl-note .v-icon {
    color: var(--v-txtMuted-base);
    margin-top: 1px;
    flex-shrink: 0;
}
</style>
